/**
 * localStorage cache for home workspace (per user + team); shared by useHomeWorkspaceCanvas and preloadService.
 */

export const HOME_WS_CACHE_V = 1;
export const HOME_WS_CACHE_PREFIX = 'trodo_home_workspace';

function readUserIdForCache() {
  try {
    const raw = localStorage.getItem('User');
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (u.id != null && u.id !== '') return String(u.id);
    return null;
  } catch {
    return null;
  }
}

export function homeWorkspaceCacheKey(teamId) {
  const uid = readUserIdForCache();
  if (!uid || teamId == null || teamId === '') return null;
  return `${HOME_WS_CACHE_PREFIX}_v${HOME_WS_CACHE_V}:${uid}:${String(teamId)}`;
}

export function readHomeWorkspaceCache(teamId) {
  const key = homeWorkspaceCacheKey(teamId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== HOME_WS_CACHE_V) return null;
    return {
      workspace: parsed.workspace ?? null,
      cards: Array.isArray(parsed.cards) ? parsed.cards : [],
      cardResults: parsed.cardResults && typeof parsed.cardResults === 'object' ? parsed.cardResults : {},
    };
  } catch {
    return null;
  }
}

export function writeHomeWorkspaceCache(teamId, snapshot) {
  const key = homeWorkspaceCacheKey(teamId);
  if (!key) return;
  try {
    const payload = {
      v: HOME_WS_CACHE_V,
      savedAt: Date.now(),
      workspace: snapshot.workspace ?? null,
      cards: Array.isArray(snapshot.cards) ? snapshot.cards : [],
      cardResults: snapshot.cardResults && typeof snapshot.cardResults === 'object' ? snapshot.cardResults : {},
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

/** Call after server-side home workspace mutations from another route so /home refetches instead of stale cache. */
export function invalidateHomeWorkspaceCache(teamId) {
  const key = homeWorkspaceCacheKey(teamId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Merge one card into the cached snapshot (fast return to Home) without a cold load. Clears that card's cached result so charts re-execute. */
export function upsertCardInHomeWorkspaceCache(teamId, card) {
  if (!card || card.id == null || teamId == null || teamId === '') return;
  const prev = readHomeWorkspaceCache(teamId);
  const workspace = prev?.workspace ?? null;
  const idStr = String(card.id);
  let cards = [...(prev?.cards || [])];
  const idx = cards.findIndex((c) => String(c.id) === idStr);
  if (idx >= 0) cards[idx] = card;
  else cards.push(card);
  const cardResults = { ...(prev?.cardResults || {}) };
  delete cardResults[idStr];
  delete cardResults[card.id];
  writeHomeWorkspaceCache(teamId, { workspace, cards, cardResults });
}
